import {
    BaseChart,
    ChartMouseHandler,
    getConfiguredInstance,
    useChart,
} from '@/components/ECharts';
import { formatKpiData } from '@/dashboards/CompetitorsMediaInvestment/helpers/formatKpiData';
import { useCompetitorsMediaInvestmentContext } from '@/dashboards/CompetitorsMediaInvestment/hooks/useCompetitorsMediaInvestmentContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { jsxToHtml } from '@/utils/reactUtils';
import { Typography } from '@analytical-alley/ui';
import EChartsReact from 'echarts-for-react';
import { isArray } from 'lodash';
import React, {
    Ref,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    AreaTooltip,
    AreaTooltipProps,
} from './AreaTooltip';
import {
    getGraphData,
    ChartOptions
} from './getGraphData';

interface GraphProps {
    chartRef?: Ref<EChartsReact | null>;
    selected: string;
    onSelected: (value: string) => void;
    getChartOptions: (chartOptionSeries: ChartOptions['series']) => void;
}

export const CompetitorsMediaSeasonalityGraph = memo(
    ({ chartRef, selected, onSelected, getChartOptions }: GraphProps) => {
        const {
            competitorsMediaInvestmentData,
            getVisibleInvestmentData,
            kpiData,
            period,
        } = useCompetitorsMediaInvestmentContext();

        const {
            dashboard: { competitorVariableName },
            contributionFormatter,
        } = useDashboardGlobalContext();

        const filteredData = useMemo(() => Object.fromEntries(
            Object.entries(getVisibleInvestmentData()).filter(
                ([key]) => key !== competitorVariableName,
            ),
        ), [getVisibleInvestmentData, competitorVariableName]);

        const eChartsOptions = useMemo(() => {
            return getGraphData({
                seriesData: filteredData,
                kpiSeriesData: formatKpiData(kpiData),
                xAxisData: competitorsMediaInvestmentData.dates,
                period: period,
            });
        }, [
            competitorsMediaInvestmentData.dates,
            kpiData,
            getVisibleInvestmentData,
            period,
            filteredData,
        ]);

        const [hovered, setHovered] = useState('');

        const selectedRef = useRef(selected);
        const timeoutRef = useRef<number | null>(null);

        useEffect(() => {
            getChartOptions(eChartsOptions.series);
        }, [eChartsOptions, getChartOptions]);

        useEffect(() => {
            selectedRef.current = selected;
        }, [selected]);

        const onClick: ChartMouseHandler = useCallback(
            (context) => {
                if (context.seriesName) {
                    onSelected(context.seriesName);
                } else {
                    onSelected('');
                }
            },
            [onSelected],
        );

        const onMouseOver: ChartMouseHandler = useCallback((context) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (context.seriesName) {
                setHovered(
                    selectedRef.current
                        ? selectedRef.current
                        : context.seriesName,
                );
            }
        }, []);

        const onMouseOut: ChartMouseHandler = useCallback(() => {
            if (selectedRef.current) {
                return;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = window.setTimeout(() => {
                setHovered('');
            }, 0);
        }, []);

        const { ref, chartOptions, onEvents, isChartReady, onChartReady } =
            useChart({
                chartOptions: eChartsOptions,
                onClick,
                onMouseOver,
                onMouseOut,
            });

        useEffect(() => {
            const instance = getConfiguredInstance(ref.current);

            if (instance) {
                instance.setOption({
                    tooltip: {
                        showDelay: selected ? 0 : 50,
                        show: true,
                        formatter: (params: AreaTooltipProps) => {
                            return jsxToHtml(
                                <AreaTooltip
                                    params={params}
                                    selectedSeriesName={hovered}
                                    contributionFormatter={
                                        contributionFormatter
                                    }
                                    clientName={competitorVariableName}
                                />,
                            );
                        },
                    },
                });
            }
        }, [
            ref,
            hovered,
            selected,
            contributionFormatter,
            competitorVariableName,
            isChartReady,
        ]);

        useEffect(() => {
            // Guarded: on mount `echarts-for-react` hands out an instance whose
            // option has not been applied yet, and a series-only partial option
            // then throws `xAxis "0" not found`.
            const instance = getConfiguredInstance(ref.current);

            const series =
                chartOptions?.series && isArray(chartOptions.series)
                    ? chartOptions.series
                    : [];

            if (instance) {
                instance.setOption({
                    series: series.map((seriesOption) => {
                        if (selected && selected !== seriesOption.name) {
                            return {
                                ...seriesOption,
                                itemStyle: { opacity: 0.1 },
                                lineStyle: { opacity: 0 },
                                symbol: 'none',
                            };
                        }
                        return {
                            ...seriesOption,
                            itemStyle: { opacity: 0.8 },
                            lineStyle: { opacity: 0.6 },
                            symbol: 'circle',
                        };
                    }),
                });
            }
        }, [ref, chartOptions.series, selected, isChartReady]);

        useImperativeHandle(chartRef, () => ref.current, [ref]);

        return (
            <div className="relative w-full h-[47.2rem]">
                <div className="absolute top-88 -left-26">
                    <Typography
                        className="-rotate-90 font-mono"
                        variant="bodyM"
                    >
                        Competitor’s investments
                    </Typography>
                </div>
                <div className="absolute top-[22rem] -right-3">
                    <Typography
                        className="-rotate-90 font-mono"
                        variant="bodyM"
                    >
                        KPI
                    </Typography>
                </div>
                <BaseChart
                    $shouldExpand
                    option={chartOptions}
                    ref={ref}
                    onEvents={onEvents}
                    onChartReady={onChartReady}
                />
            </div>
        );
    },
);

CompetitorsMediaSeasonalityGraph.displayName =
    'CompetitorsMediaSeasonalityGraph';
